import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import './responsive.css'; // Import responsive styles
import { AuthProvider } from './context/AuthContext'; // Import AuthProvider
import { HelmetProvider } from 'react-helmet-async';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore'; // Added onSnapshot

import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import TentangKamiPage from './pages/TentangKamiPage';
import SambutanPage from './pages/SambutanPage';
import VisiMisiPage from './pages/VisiMisiPage';
import SejarahPage from './pages/SejarahPage';
import StrukturPage from './pages/StrukturPage';
import GuruStaffPage from './pages/GuruStaffPage';
import JurusanPage from './pages/JurusanPage';
import JurusanDetailPage from './pages/JurusanDetailPage';
import RuangPublikPage from './pages/RuangPublikPage';
import PengembanganAkademikPage from './pages/PengembanganAkademikPage';
import KontakPage from './pages/KontakPage';
import SPMBPage from './pages/SPMBPage';
import GaleriPage from './pages/GaleriPage';
import GaleriAlbumPage from './pages/GaleriAlbumPage';
import GaleriFavoritPage from './pages/GaleriFavoritPage';
import BeritaPage from './pages/BeritaPage';
import BeritaDetailPage from './pages/BeritaDetailPage';
import EkstrakurikulerPage from './pages/EkstrakurikulerPage';
import PerhotelanPage from './pages/PerhotelanPage';
import PariwisataPage from './pages/PariwisataPage';
import UnitKerjaPage from './pages/UnitKerjaPage';
import KurikulumPage from './pages/KurikulumPage';
import KesiswaanPage from './pages/KesiswaanPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import MaintenancePage from './pages/MaintenancePage'; // Import Maintenance Page
import ProtectedRoute from './components/ProtectedRoute';
import WelcomeModal from './components/WelcomeModal';
import PrestasiPage from './pages/PrestasiPage';
import AlumniLulusPage from './pages/AlumniLulusPage';
import Preloader from './components/Preloader';
import VisitorTracker from './components/VisitorTracker';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isWelcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false); // State for Maintenance Mode
  const [welcomeModalPosters, setWelcomeModalPosters] = useState([]);

  const handlePreloaderFinish = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    const settingsRef = doc(db, 'settings', 'siteConfig');
    const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const settings = docSnap.data();
        if (settings?.welcomeModal?.isEnabled && settings?.welcomeModal?.posters?.length > 0) {
          setWelcomeModalPosters(settings.welcomeModal.posters);
          // Check sessionStorage to determine if modal should be shown
          const modalShown = sessionStorage.getItem('welcomeModalShown');
          if (!modalShown) {
            setWelcomeModalOpen(true);
          }
        } else {
          setWelcomeModalOpen(false); // Close if disabled
          setWelcomeModalPosters([]);
        }

        // Maintenance Mode Check
        if (settings?.isMaintenanceMode) {
          setIsMaintenanceMode(true);
        } else {
          setIsMaintenanceMode(false);
        }
      }
    }, (error) => {
      console.error("Error listening to welcome modal settings:", error);
    });

    return () => unsubscribe();
  }, []);

  if (isMaintenanceMode && !window.location.pathname.startsWith('/admin')) {
    return (
      <Router>
        <MaintenancePage />
      </Router>
    );
  }

  const handleCloseWelcomeModal = () => {
    setWelcomeModalOpen(false);
    sessionStorage.setItem('welcomeModalShown', 'true'); // Tandai bahwa modal sudah ditampilkan
  };

  return (
    <Router>
      <HelmetProvider>
        <>
          {isLoading && <Preloader onFinish={handlePreloaderFinish} />}
          <VisitorTracker />
          <WelcomeModal
            isOpen={isWelcomeModalOpen}
            onClose={handleCloseWelcomeModal}
            posters={welcomeModalPosters}
          />
          <div className="main-content-fade-in">
            <AuthProvider>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="tentang-kami" element={<TentangKamiPage />} />
                  <Route path="tentang-kami/sambutan" element={<SambutanPage />} />
                  <Route path="tentang-kami/visi-misi" element={<VisiMisiPage />} />
                  <Route path="tentang-kami/sejarah" element={<SejarahPage />} />
                  <Route path="tentang-kami/struktur" element={<StrukturPage />} />
                  <Route path="tentang-kami/guru-staff" element={<GuruStaffPage />} />
                  <Route path="jurusan" element={<JurusanPage />} />
                  <Route path="jurusan/:id" element={<JurusanDetailPage />} />
                  <Route path="galeri" element={<GaleriPage />} />
                  <Route path="galeri/favorit" element={<GaleriFavoritPage />} />
                  <Route path="galeri/:categorySlug" element={<GaleriAlbumPage />} />
                  <Route path="berita" element={<BeritaPage />} />
                  <Route path="berita/:slug" element={<BeritaDetailPage />} />
                  <Route path="/prestasi" element={<PrestasiPage />} />
                  <Route path="/alumni-lulus" element={<AlumniLulusPage />} />
                  <Route path="ekstrakurikuler" element={<EkstrakurikulerPage />} />
                  <Route path="pengembangan-akademik" element={<PengembanganAkademikPage />} />
                  <Route path="ruang-publik" element={<RuangPublikPage />} />
                  <Route path="kontak" element={<KontakPage />} />
                  <Route path="aktivitas/perhotelan" element={<PerhotelanPage />} />
                  <Route path="aktivitas/pariwisata" element={<PariwisataPage />} />
                  <Route path="kurikulum" element={<KurikulumPage />} />
                  <Route path="kesiswaan" element={<KesiswaanPage />} />
                  <Route path=":unitId" element={<UnitKerjaPage />} />
                  <Route path="spmb" element={<SPMBPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              </Routes>
            </AuthProvider>
          </div>
        </>
      </HelmetProvider>

    </Router>
  );
}

export default App;
